import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Container, Stack, Typography } from '@mui/material'
import { Button, Skeleton } from '@repo/ui'
import { UserPreview } from 'components/UI'
import { createApiCall } from 'services'
import { ROUTES } from 'routes'

/**
 * VRG-358 — where a profile QR resolves when it is scanned by a PHONE'S OWN
 * CAMERA rather than by the in-app scanner.
 *
 * Why this route exists at all. The QR encodes `{origin}/u/{code}` rather than
 * the bare code, because a QR is most often read by whatever camera is to hand
 * and a bare opaque string shows the scanner a meaningless blob. Encoding a URL
 * without serving it would be worse still: the friendly path would 404. So the
 * URL form obliges this page.
 *
 * It is deliberately NOT the primary path. The designed flow is Alice already
 * inside the gift drawer, scanning Bob's code with the in-app scanner, which
 * populates the recipient without leaving the flow. This page serves the other
 * order of events — someone scanned first, and arrives cold.
 *
 * Its job is therefore CONFIRMATION OF IDENTITY, which is the thing the QR was
 * asked for: "scan a profile QR code to confirm it is them." It shows who the
 * code belongs to and offers a way into the gift flow. It does NOT pre-fill a
 * recipient across a cold start — carrying a pending recipient through sign-in,
 * the certificates list and into a specific certificate's gift drawer is a
 * different feature, and a half-built version of it would strand people
 * mid-flow.
 *
 * PUBLIC, no auth guard, and that is correct: possession of the code is the
 * authorization, and what it reveals (name, @handle, avatar) is exactly what a
 * @handle lookup already reveals to any signed-in user. It reveals no email and
 * no phone — see the VRG-357 projection rule, which this endpoint obeys.
 */

type ResolvedPerson = {
  id: string
  first_name: string | null
  last_name: string | null
  username: string | null
  avatar: string | null
}

type LoadState =
  | { status: 'loading' }
  | { status: 'found'; person: ResolvedPerson }
  | { status: 'not-found' }

export default function ProfileCode() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    const resolve = async () => {
      if (!code) {
        setState({ status: 'not-found' })
        return
      }

      // NOTE: `createApiCall` does NOT reject on an HTTP error — its
      // errorHandler resolves with `error.response` unless `throwError: true`.
      // So a 404 arrives here as a perfectly ordinary resolved promise, and a
      // try/catch around this would never fire. Validate the payload shape
      // instead, which also covers the offline case (errorHandler returns a
      // serialized error object with no `data.id`).
      const res = await createApiCall({
        method: 'GET',
        url: `/auth/profile-code/${encodeURIComponent(code)}`,
      })

      if (cancelled) return

      const person = res?.data
      if (res?.status === 200 && person && typeof person.id === 'string') {
        setState({ status: 'found', person })
      } else {
        setState({ status: 'not-found' })
      }
    }

    void resolve()
    return () => {
      cancelled = true
    }
  }, [code])

  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        {state.status === 'loading' && (
          // §19: the skeleton occupies the footprint the resolved card will
          // occupy (a 40px avatar row plus two lines), so nothing shifts when
          // the answer arrives.
          <Stack spacing={2} alignItems='center' sx={{ width: '100%' }}>
            <Skeleton variant='circular' width={40} height={40} />
            <Skeleton width={180} height={24} />
            <Skeleton width={240} height={20} />
          </Stack>
        )}

        {state.status === 'not-found' && (
          <Stack spacing={2} alignItems='center'>
            <Typography variant='h4' component='h1'>
              This code didn&apos;t match anyone
            </Typography>
            {/* Separate sentences, not em-dash-joined (§15), lineHeight 1.5. */}
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ lineHeight: 1.5 }}
            >
              It may have been reset since it was shared. Ask them to open Vera
              and show you their code again.
            </Typography>
          </Stack>
        )}

        {state.status === 'found' && (
          <Stack spacing={3} alignItems='center' sx={{ width: '100%' }}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <UserPreview
                user={state.person}
                avatarUrl={state.person.avatar}
                size={56}
              />
            </Stack>

            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ lineHeight: 1.5 }}
            >
              This is their Vera code. Open one of your certificates to send it
              to them.
            </Typography>

            <Button
              variant='primary'
              fullWidth
              onClick={() => navigate(ROUTES.CERTIFICATES)}
            >
              My certificates
            </Button>
          </Stack>
        )}
      </Box>
    </Container>
  )
}
